import React from 'react'
import { DSButton, DSSpacer, DSText } from '@zoe/ds-web'
import { onBackClickCallback, onNextClickType } from 'components/types'
import { ContextAwareText } from '../text/ContextAwareText'
import { ScreenContainer } from '../layout/ScreenContainer'
import { appConfig } from '../../../appConfig'

type Section = {
  title: string
  description: string
  items?: string[]
  footer?: string
}

export type MenoscaleConsentScreenProps = {
  title: string
  questionKey: string
  description: string
  sections: Section[]
  acceptButtonLabel: string
  declineButtonLabel: string
}

type Props = MenoscaleConsentScreenProps & {
  onNextClick: onNextClickType
  onBackClick: onBackClickCallback
}

export const MenoscaleConsentScreen: React.FC<Props> = ({
  title,
  questionKey,
  description,
  sections,
  acceptButtonLabel,
  declineButtonLabel,
  onNextClick,
}) => {
  const menoscaleFaq = (
    <a
      href={`${appConfig.zoeWebsiteBaseUri}/menoscale#faq`}
      rel="noreferrer"
      target="_blank"
      style={{ color: 'var(--colour-text-link)', fontWeight: 700 }}
    >
      FAQ
    </a>
  )

  return (
    <ScreenContainer>
      <DSText variant="fluid-heading-700" weight="semiBold">
        {title}
      </DSText>
      <DSSpacer size={24} direction="vertical" />
      <DSText variant="fluid-heading-500" weight="light" color="secondary">
        <ContextAwareText text={description} />
      </DSText>
      {sections.map((section, index) => (
        <React.Fragment key={index}>
          <DSSpacer size={24} direction="vertical" />
          <DSText variant="fluid-heading-500" weight="semiBold">
            <ContextAwareText text={section.title} />
          </DSText>
          <DSSpacer size={8} direction="vertical" />
          <DSText variant="fluid-paragraph-200" weight="light">
            <ContextAwareText text={section.description} additionalTokens={{ menoscaleFaq }} />
          </DSText>
          {section.items && (
            <ul style={{ listStyleType: 'disc', paddingLeft: 'var(--grid-24)', marginTop: 'var(--grid-8)' }}>
              {section.items.map((item, idx) => (
                <li key={idx} style={{ fontWeight: 300, fontSize: '1rem' }}>
                  <ContextAwareText text={item} />
                </li>
              ))}
            </ul>
          )}
          {section.footer && (
            <>
              <DSSpacer size={8} direction="vertical" />
              <DSText variant="fluid-paragraph-200" weight="light">
                <ContextAwareText text={section.footer} />
              </DSText>
            </>
          )}
        </React.Fragment>
      ))}
      <DSSpacer size={16} direction="vertical" />
      <DSButton
        variant="primary"
        colorScheme="default"
        size="large"
        width="full"
        testId="accept-button"
        onPress={() => onNextClick({ [questionKey]: true })}
      >
        {acceptButtonLabel}
      </DSButton>
      <DSSpacer size={16} direction="vertical" />
      <DSButton
        variant="secondary"
        colorScheme="neutral"
        size="large"
        width="full"
        testId="decline-button"
        onPress={() => onNextClick({ [questionKey]: false })}
      >
        {declineButtonLabel}
      </DSButton>
      <DSSpacer size={16} direction="vertical" />
    </ScreenContainer>
  )
}
